import { useState, FC } from 'react'
import { ResolvedFunction } from '../../types/functions'
import { Copy, Check, Terminal, Code2 } from 'lucide-react'

interface FunctionCodePanelProps {
  func: ResolvedFunction
}

export const FunctionCodePanel: FC<FunctionCodePanelProps> = ({ func }) => {
  const [activeTab, setActiveTab] = useState<'step' | 'response' | 'tsdoc'>('step')
  const [copied, setCopied] = useState(false)

  const hasResponseExample = !!func.responsePayloadExample
  const hasTsdocExamples = func.examples && func.examples.length > 0

  const getCurrentContent = () => {
    switch (activeTab) {
      case 'step':
        return func.stepPayloadExample
      case 'response':
        return func.responsePayloadExample || ''
      case 'tsdoc':
        return func.examples.join('\n\n')
      default:
        return func.stepPayloadExample
    }
  }

  const handleCopy = () => {
    const text = getCurrentContent()
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const content = getCurrentContent()
  const lines = content.split('\n')

  return (
    <div className="ns-code-panel">
      <div className="ns-code-panel-header">
        <div className="ns-code-panel-tabs">
          <button
            type="button"
            onClick={() => setActiveTab('step')}
            className={`ns-code-tab ${activeTab === 'step' ? 'is-active' : ''}`}
          >
            <Terminal size={14} />
            <span>Script Step JSON</span>
          </button>

          {hasResponseExample && (
            <button
              type="button"
              onClick={() => setActiveTab('response')}
              className={`ns-code-tab ${activeTab === 'response' ? 'is-active' : ''}`}
            >
              <Code2 size={14} />
              <span>Response Schema</span>
            </button>
          )}

          {hasTsdocExamples && (
            <button
              type="button"
              onClick={() => setActiveTab('tsdoc')}
              className={`ns-code-tab ${activeTab === 'tsdoc' ? 'is-active' : ''}`}
            >
              <Code2 size={14} />
              <span>TSDoc Example</span>
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={handleCopy}
          className="ns-code-copy-btn"
          title="Copy code to clipboard"
        >
          {copied ? (
            <>
              <Check size={14} className="text-forest" />
              <span>Copied</span>
            </>
          ) : (
            <>
              <Copy size={14} />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      <div className="ns-code-panel-body">
        <pre className="ns-code-pre">
          <code>
            {lines.map((line, idx) => (
              <div key={idx} className="ns-code-line">
                <span className="ns-line-number">{idx + 1}</span>
                <span className="ns-line-text">{line}</span>
              </div>
            ))}
          </code>
        </pre>
      </div>
    </div>
  )
}
